/**
 * AllUserDetails is a React functional component that manages and displays detailed information about users, nodes, pumps, and their interactions.
 *
 * It includes state management for UI elements, data fetching, and form validation. The component renders a table of users with options to view, edit, and remove users.
 *
 * State:
 * - menu: Boolean for toggling mobile menu visibility.
 * - currentPage: Integer for current page in pagination.
 * - templeList: Array storing node data.
 * - hideContent: Boolean for toggling visibility of content sections.
 * - modalShow: Boolean for controlling modal visibility.
 * - EditModal: Boolean for triggering edit modals.
 * - NodeAdd: Boolean for adding nodes.
 * - AddPump: Boolean for adding pumps.
 * - editTemple: Object for storing temple data to be edited.
 * - searchQuery: String for storing search input.
 * - pageSize: Integer for pagination page size.
 * - master: Array for storing master data.
 * - FetchMaster: Array for storing fetched master data.
 * - userId: Integer for storing the current user ID.
 * - loading: Boolean for tracking data loading state.
 * - error: Boolean for tracking error state.
 * - NodeModalData: Object for storing node modal data.
 * - NodeEdit: String for storing node name being edited.
 * - editIndex: Integer for tracking the current edit index.
 * - editedValves: Array for tracking edited valve data.
 * - pumpName, pumpPhase, pumpID, pumpHP: Strings for storing pump information.
 * - errors: Object for storing validation errors.
 * - NodeValveEditValue: Array for storing node valve edit values.
 * - EditNameValve: String for storing the node name being edited.
 * - PumpEdit, EditNamePump, EditHpPump: Strings for storing pump edit information.
 * - removeUserID: Object for storing the user ID to be removed.
 *
 * Methods:
 * - toggleMobileMenu: Toggles the mobile menu visibility.
 * - fetchMaster: Asynchronously fetches master data from the API and updates the state.
 * - topRight: Displays a message at the top-right position using alertify.
 * - onPageChange: Updates the current page in pagination.
 * - onShowSizeChange: Updates the page size in pagination.
 * - exportToCSV: Exports the fetched master data to a CSV file.
 * - validateField: Validates individual form fields.
 * - validateForm: Validates the entire form before submission.
 * - handleSubmit: Handles the pump form submission, validates, and sends data to the server.
 * - handleUpdate: Updates the edited valve data on the server.
 * - handleNameChange, handleNameSubmit: Handle editing and submitting node names.
 * - handlePumpNameChange, handlePumpHPChange, handlePumpNameSubmit: Handle editing and submitting pump information.
 * - handleRemoveUserSubmit: Handles the removal of a user from the master list.
 */

import React, { useState, useEffect } from "react";
import Header from "../../layouts/Header";
import Sidebar from "../../layouts/Sidebar";
import "../../_components/antd.css";
import { Button, Drawer, Modal, notification, Switch, Table } from "antd";
import "../../assets/css/AddedCss.css";

import UserDetailEdit from "./MasterEdit";
import { Link, useHistory } from "react-router-dom/cjs/react-router-dom";
// import { MasterFull } from "./master";
import MasterAdd from "./MasterAdd";
import {
  AddPumpServer,
  DeleteUser,
  EditNode,
  EditPump,
  EditValve,
} from "../../services/apiUtils";
// import axios from "axios";
import ApiService from "../../services/httpService";

const AllUserDetails = () => {
  const [menu, setMenu] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [templeList, setTempleList] = useState([]);
  const [hideContent, setHideContent] = useState(true);
  const [modalShow, setModalShow] = useState(false);
  const [EditModal, setEditModal] = useState(false);
  const [NodeAdd, setNodeAdd] = useState(false);
  const [AddPump, setAddPump] = useState(false);
  const [editTemple, setEditTemple] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [master, setMaster] = useState([]);

  // master fetch
  const [FetchMaster, setFetchMaster] = useState([]);

  // user id
  const [userId, setUserId] = useState(null);

  // const [user] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // node modal
  const [NodeModalData, setNodeModalData] = useState(null);
  const [NodeEdit, setNodeEdit] = useState("");
  const [editNodeModalOpen, setEditNodeModalOpen] = useState(false);

  // node valve edit
  const [editIndex, setEditIndex] = useState(null);
  const [editedValves, setEditedValves] = useState([]);

  // pump
  const [pumpName, setPumpName] = useState("");
  const [pumpPhase, setPumpPhase] = useState("");
  const [pumpID, setPumpID] = useState("");
  const [pumpHP, setPumpHP] = useState("");
  const [errors, setErrors] = useState({});

  // node valve edit
  const [NodeValveEditValue, setNodeValveEditValue] = useState([]);

  // node name edit
  const [EditNameValve, setEditNameValve] = useState("");

  // Pump modal
  const [PumpEdit, setPumpEdit] = useState("");
  // const [PumpHpEdit, setPumpHpEdit] = useState(0);
  const [EditNamePump, setEditNamePump] = useState("");
  const [EditHpPump, setEditHpPump] = useState("");

  const [editPumpModalOpen, setEditPumpModalOpen] = useState(false);

  // user remove
  const [removeUserID, setRemoveUserID] = useState();
  const [removeUserModalOpen, setRemoveUserModalOpen] = useState(false);

  // history
  const history = useHistory();

  const toggleMobileMenu = () => {
    setMenu(!menu);
  };

  const fetchMaster = async () => {
    setLoading(true);
    try {
      const response = await ApiService.getPublic(`api/CenPilot/MasterList`);

      if (!response || response.length === 0) {
        console.warn("No data received from API");
        setFetchMaster([]);
        setError("No data found");
        notification.error({
          message: "Warning",
          description: "No data found",
        });
        return;
      } else if (response === "Invalid Device ID.") {
        // Handle Invalid Device ID here
        alert("Invalid Device ID. Please log in again.");
        notification.error({
          message: "Error",
          description: "Invalid Device ID. Please log in again.",
        });
        localStorage.clear();
        history.push("/login");
        return;
      } else {
        console.log("Data for Master:", response);
        setFetchMaster(response);
        setError("");
      }
    } catch (err) {
      console.error("Error fetching Master:", err);

      // Check if error response contains "Invalid Device ID."
      if (
        err?.response?.data === "Invalid Device ID." ||
        err?.message?.includes("Invalid Device ID")
      ) {
        alert("Invalid Device ID. Please log in again.");
        notification.error({
          message: "Error",
          description: "Invalid Device ID. Please log in again.",
        });
        localStorage.clear();
        history.push("/login");
        return;
      }

      setError("Failed to fetch Master");
      notification.error({
        message: "Error",
        description: "Failed to fetch Master" + err,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const user = Number(localStorage.getItem("user_id"));
    if (user) {
      setUserId(user);
      console.log("user", user);

      fetchMaster();
    }
  }, [EditModal, hideContent]);

  const columns = [
    {
      title: "#",
      dataIndex: "ID",
      key: "ID",
      sorter: (a, b) => a.ID - b.ID,
    },
    {
      title: "Name",
      dataIndex: "Name",
      key: "Name",
      render: (text, record) => (
        <h2 className="table-avatar">
          <div to="/profile">{record.Name}</div>
        </h2>
      ),
      sorter: (a, b) => a.Name.localeCompare(b.Name),
    },
    {
      title: "Location",
      dataIndex: "Location",
      key: "Location",
      render: (text, record) => {
        // Check if Location exists and is a valid string
        if (!record.Location || typeof record.Location !== "string") {
          return <span>No Location available</span>;
        }

        // Split latitude and longitude
        const LocationParts = record.Location.split(",");
        if (LocationParts.length !== 2) {
          return <span>Invalid Location format</span>;
        }

        // Trim spaces & convert to numbers
        const [latitude, longitude] = LocationParts.map((coord) =>
          coord.trim()
        );

        // Generate Google Maps link
        const mapsLink = `https://www.google.com/maps?q=${latitude},${longitude}`;

        return (
          <a href={mapsLink} target="_blank" rel="noopener noreferrer">
            View Location
          </a>
        );
      },
    },
    {
      title: "Village",
      dataIndex: "Village",
      key: "Village",
      sorter: (a, b) => a.Village.localeCompare(b.Village),
    },
    {
      title: "City",
      dataIndex: "City",
      key: "City",
      sorter: (a, b) => a.City.localeCompare(b.City),
    },
    {
      title: "Status",
      dataIndex: "Status",
      key: "Status",
      sorter: (a, b) => a.Status - b.Status,
      render: (status) => (status === 1 ? "Active" : "Inactive"),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (text, record) => (
        <div className="d-flex align-items-center">
          <div className="dropdown dropdown-action">
            <button
              className="btn-action-icon border-0"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="fas fa-ellipsis-v"></i>
            </button>
            <div className="dropdown-menu dropdown-menu-right">
              <ul>
                <li>
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      setTempleList(record);
                      setHideContent(!hideContent);
                      setModalShow(!modalShow);
                    }}
                  >
                    <i className="far fa-eye me-2"></i>View
                  </button>
                </li>
                <li>
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      setEditTemple(record);
                      setEditModal(true);
                    }}
                  >
                    <i className="far fa-edit me-2"></i>Edit
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      ),
    },
  ];

  // Handler when page changes
  const onPageChange = (page) => {
    setCurrentPage(page);
  };

  const onShowSizeChange = (current, size) => {
    setPageSize(size);
  };

  const filteredMasterList = Array.isArray(FetchMaster)
    ? FetchMaster.filter((master) => {
        const searchLower = searchQuery.toLowerCase();
        return (
          (master.City && master.City.toLowerCase().includes(searchLower)) ||
          (master.MasterId &&
            master.MasterId.toLowerCase().includes(searchLower)) ||
          (master.Name && master.Name.toLowerCase().includes(searchLower)) ||
          (master.Status !== undefined &&
            master.Status.toString()
              .toLowerCase()
              .includes(searchLower)) || // Convert number to string
          (master.Village &&
            master.Village.toLowerCase().includes(searchLower)) ||
          (master.ID !== undefined &&
            master.ID.toString()
              .toLowerCase()
              .includes(searchLower)) // Convert number to string
        );
      })
    : [];

  // Paginate filtered temples
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedTemples = filteredMasterList.slice(startIndex, endIndex);

  // Validate individual fields
  const validateField = (name, value) => {
    let error = "";

    if (name === "pumpName" && !value.trim()) {
      error = "Pump Name is required";
    }

    if (name === "pumpID" && !value.trim()) {
      error = "Pump ID is required";
    }

    if (name === "pumpPhase" && !value) {
      error = "Please select a Phase Type";
    }

    if (name === "pumpHP") {
      if (!value) {
        error = "Pump HP is required";
      } else if (isNaN(value)) {
        error = "Pump HP must be a number";
      }
    }

    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: error,
    }));
  };

  // Validate all fields before submitting
  const validateForm = () => {
    let errors = {};

    if (!pumpName.trim()) errors.pumpName = "Pump Name is required";
    if (!pumpID.trim()) errors.pumpID = "Pump ID is required";
    if (!pumpPhase) errors.pumpPhase = "Please select a Phase Type";

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const FullPumpData = {
        Created_by: userId,
        MasterId: templeList.MasterId,
        PumpID: pumpID.trim(),
        PumpName: pumpName.trim(),
        Phase: pumpPhase === "Three Phase" ? 3 : 1,
        Status: 1,
        Hp: pumpHP.trim(),
      };

      console.log("FullPumpData", FullPumpData);

      const PumpAdd = await AddPumpServer(FullPumpData);
      console.log("Pump Add", PumpAdd);

      notification.success({
        message: "Success",
        description: "Pump Registration Successful!",
      });

      // Reset form
      setAddPump(false);
      setModalShow(false);
      setPumpName("");
      setPumpID("");
      setPumpHP("");
      setPumpPhase("");
      setErrors({});
      setHideContent(!hideContent);
    } catch (error) {
      console.log(error);
      notification.error({
        message: "Error",
        description: "Pump Registration Error" + error,
      });
    }
  };

  // node
  const columnsNode = [
    {
      title: "Node ID",
      dataIndex: "NodeID",
      key: "NodeID",
    },
    {
      title: "Node Name",
      dataIndex: "NodeName",
      key: "NodeName",
    },
    {
      title: "Valves Count",
      dataIndex: "valvesCount",
      key: "valvesCount",
    },

    {
      title: "Valves",
      dataIndex: "valves",
      key: "valves",
      render: (valves) =>
        valves.length > 0
          ? valves.map((valve) => `${valve.name} (${valve.status})`).join(", ")
          : "No Valves",
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (text, record) => (
        <div className="d-flex align-items-center">
          <div className="dropdown dropdown-action">
            <button
              className="btn-action-icon border-0"
              data-bs-toggle="dropdown"
              aria-expanded="false"
            >
              <i className="fas fa-ellipsis-v"></i>
            </button>
            <div className="dropdown-menu dropdown-menu-right">
              <ul>
                <li>
                  <button
                    className="dropdown-item"
                    data-bs-toggle="modal"
                    data-bs-target="#exampleModal"
                    onClick={() => {
                      console.log("record", record);
                      setNodeModalData(record);
                    }}
                  >
                    <i className="far fa-eye me-2"></i>View
                  </button>
                </li>
                <li>
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      console.log("record NodeName", record.NodeName);
                      setNodeEdit(record.NodeName);
                      setEditNameValve(record);
                      setEditNodeModalOpen(true);
                    }}
                  >
                    <i className="far fa-edit me-2"></i>
                    Edit
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      ),
    },
  ];

  const dataSource = (templeList?.NodeData ?? []).map((node) => ({
    key: node.NodeID,
    NodeID: node.NodeID,
    Id: node.ID,
    NodeName: node.NodeName,
    valvesCount: node.ValveData ? node.ValveData.length : 0,
    valves:
      node.ValveData?.map((valve) => ({
        id: valve.ID,
        ValveID: valve.ValveID,
        name: valve.ValveName,
        status: valve.Status === 1 ? "Active" : "Inactive",
      })) ?? [],
  }));

  // node valve  edit
  useEffect(() => {
    setEditedValves(NodeModalData?.valves || []);
  }, [NodeModalData]);

  const handleInputChange = (index, field, value) => {
    const updatedValves = [...editedValves];
    updatedValves[index] = { ...updatedValves[index], [field]: value };
    setEditedValves(updatedValves);
  };

  // node edit
  const handleUpdate = async () => {
    if (editIndex !== null) {
      try {
        const fullData = { ...editedValves[editIndex] };

        // Validation: Check if Valve Name is empty
        if (!fullData.name.trim()) {
          alert("Valve name cannot be empty.");
          return;
        }

        console.log("Edited Valve:", fullData);
        console.log("Node Valve Edit Value", NodeValveEditValue);

        const ServerData = {
          Id: NodeValveEditValue.id,
          ValveName: fullData.name,
          Status: fullData.status === "Active" ? 1 : 0,
          ValveID: fullData.ValveID,
        };

        console.log("Server Data", ServerData);
        const Valve = await EditValve(ServerData);
        console.log("Valve", Valve);

        // Reset the edit index and close modal
        setEditIndex(null);
        document.querySelector("#exampleModal .btn-close")?.click();
        setEditedValves([]);
        setNodeEdit("");
        setHideContent(true);
        setModalShow(false);
        notification.success({
          message: "Successful",
          description: "Valve updated successfully...",
        });
      } catch (error) {
        console.log("Error:", error);
        notification.error({
          message: "Error",
          description: "Valve update error" + error,
        });
      }
    }
  };

  const handleNameChange = (e) => {
    setNodeEdit(e.target.value);
  };

  // Handle form submission
  const handleNameSubmit = async () => {
    if (!NodeEdit.trim()) {
      notification.error({
        message: "Error",
        description: "Valve name cannot be empty.",
      });
      return;
    }
    try {
      console.log("Edit Name Node", EditNameValve);
      console.log("Edited Node Name:", NodeEdit);
      const ServerData = {
        Id: EditNameValve.Id,
        NodeName: NodeEdit.trim(),
      };
      const Node = await EditNode(ServerData);
      console.log("Node", Node);
      document.querySelector("#exampleModal .btn-close")?.click();
      setHideContent(true);
      setModalShow(false);
      notification.success({
        message: "Successful",
        description: "Node Name Change Successful",
      });
    } catch (error) {
      console.log("error:", error);
      notification.error({
        message: "Error",
        description: "Error:" + error,
      });
    }
  };

  // Handle form submission for pump

  const handlePumpNameChange = (e) => {
    setPumpEdit(e.target.value);
  };
  const handlePumpHPChange = (e) => {
    setEditHpPump(e.target.value);
  };

  const handlePumpNameSubmit = async () => {
    if (!PumpEdit.trim()) {
      notification.error({
        message: "Error",
        description: "Pump name cannot be empty.",
      });
      return;
    }
    if (!EditHpPump.trim()) {
      notification.error({
        message: "Error",
        description: "Pump name cannot be empty.",
      });
      return;
    }

    if (!/^\d+(\.\d+)?$/.test(EditHpPump)) {
      notification.error({
        message: "Error",
        description: "Pump HP must be a valid number.",
      });
      return;
    }
    try {
      console.log("Edit Name Pump", EditNamePump);

      const FullCode = {
        ID: EditNamePump.ID,
        Status: EditNamePump.Status,
        PumpName: PumpEdit,
        HP: EditHpPump,
      };

      console.log("Full Code", FullCode);

      const Pump = await EditPump(FullCode);
      console.log("Pump", Pump);
      if (Pump.success === 200) {
        document.querySelector("PumpModalExample .btn-close")?.click();
        setHideContent(true);
        setModalShow(false);
        notification.success({
          message: "Successful",
          description: "Pump Name Change Successful",
        });
      } else {
        notification.error({
          message: "Error",
        });
        return;
      }
    } catch (error) {
      console.log("error:", error);
      notification.error({
        message: "Error",
        description: "Error:" + error,
      });
    }
  };

  const handleRemoveUserSubmit = async () => {
    try {
      const ServerData = {
        ID: removeUserID.ID,
        Master: templeList.MasterId,
      };
      console.log("Server Data", ServerData);
      const User = await DeleteUser(ServerData);
      console.log("User", User);
      setHideContent(true);
      setModalShow(false);
      notification.success({
        message: "Successful",
        description: "User Delete Sucessfully...",
      });
    } catch (error) {
      console.log("error:", error);
      notification.error({
        message: "Error",
        description: "Error:" + error,
      });
    }
  };

  return (
    <>
      <div className={`main-wrapper ${menu ? "slide-nav" : ""}`}>
        <Header onMenuClick={() => toggleMobileMenu()} />
        <Sidebar />

        {hideContent && (
          <div className="page-wrapper">
            <div className="content container-fluid">
              {/* Page Header */}
              <div className="page-header">
                <div className="content-page-header">
                  <h5>Master Details</h5>
                  <div className="list-btn">
                    <ul className="filter-list">
                      <li>
                        <input
                          type="text"
                          name="search_temple"
                          placeholder="Search..."
                          className="form-control"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                      </li>
                      <li>
                        <Link to="/masterregister">
                          <div className="btn btn-primary">
                            <i
                              className="fa fa-plus-circle me-2"
                              aria-hidden="true"
                            />
                            Register
                          </div>
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              {/* /Page Header */}

              {/* Table */}
              <div className="row">
                <div className="col-sm-12">
                  <div className="card-table">
                    <div className="card-body purchase">
                      <div className="table-responsive table-hover">
                        {loading && (
                          <div
                            className="spinner-border text-success loading-main"
                            role="status"
                          />
                        )}
                        {error && <p className="error-main">{error}</p>}
                        {!loading && !error && (
                          <Table
                            pagination={{
                              total: FetchMaster.length,
                              current: currentPage,
                              pageSize: pageSize,
                              showSizeChanger: true,
                              onChange: onPageChange,
                              onShowSizeChange: onShowSizeChange,
                            }}
                            columns={columns}
                            dataSource={paginatedTemples.map((item, index) => ({
                              ...item,
                              key: index,
                            }))}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* /Table */}
            </div>
          </div>
        )}

        {/* <!-- Modal 1 --> */}
        {modalShow && (
          <>
            <div className="page-wrapper">
              <div className="content container-fluid">
                <div className="row justify-content-lg-center">
                  <div className="col-lg-10">
                    {/* Page Header */}
                    <div className="page-header">
                      <div className="content-page-header d-flex justify-content-start">
                        <button
                          type="button"
                          className="btn btn-link me-1"
                          onClick={() => {
                            setHideContent(true);
                            setModalShow(false);
                          }}
                        >
                          Back
                        </button>
                        <h5>Master</h5>
                      </div>
                    </div>

                    {/* /Page Header */}
                    {templeList && (
                      <>
                        <div className="profile-cover">
                          <div className="profile-cover-wrap">
                            <img
                              className="profile-cover-img"
                              src={`https://t4.ftcdn.net/jpg/09/55/30/97/360_F_955309715_f8Q2l7zaPDHC9FBiwWrzGhOT8tOntpx4.jpg`}
                              alt="Profile Cover"
                              id="cover-image"
                            />
                          </div>
                        </div>

                        <div className="text-center mb-5">
                          <label
                            className="avatar avatar-xxl profile-cover-avatar"
                            htmlFor="avatar_upload"
                          >
                            <img
                              className="avatar-img"
                              src={`https://t4.ftcdn.net/jpg/09/55/30/97/360_F_955309715_f8Q2l7zaPDHC9FBiwWrzGhOT8tOntpx4.jpg`}
                              alt="Profile Image"
                              id="blah"
                            />
                          </label>
                          <h2>{templeList.Name}</h2>
                        </div>

                        <div className="row">
                          {/* Profile Details */}
                          <div className="col-lg-4">
                            <div className="card">
                              <div className="card-header">
                                <h5 className="card-title">Master</h5>
                              </div>
                              <div className="card-body">
                                <ul className="list-unstyled mb-0 d-flex flex-column gap-1">
                                  <li className="pt-2 pb-0">
                                    <h6>Address</h6>
                                  </li>
                                  <li>
                                    {templeList.Village}, {templeList.City}
                                  </li>
                                  <li className="pt-2 pb-0">
                                    <h6>Location</h6>
                                  </li>
                                  <li>
                                    {templeList.Location ? (
                                      (() => {
                                        const locationParts = templeList.Location.split(
                                          ","
                                        );
                                        if (locationParts.length !== 2) {
                                          return (
                                            <span>Invalid location format</span>
                                          );
                                        }

                                        const [
                                          latitude,
                                          longitude,
                                        ] = locationParts.map((coord) =>
                                          coord.trim()
                                        );

                                        return (
                                          <a
                                            href={`https://www.google.com/maps?q=${latitude},${longitude}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            <i className="fa-solid fa-map"></i>
                                            View on Map
                                          </a>
                                        );
                                      })()
                                    ) : (
                                      <span>No location available</span>
                                    )}
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </div>

                          {/* Master Node Details */}
                          <div className="col-lg-8">
                            <div className="card">
                              <div className="card-header d-flex justify-content-between">
                                <h5 className="card-title">Node Details</h5>
                                <div className="d-flex gap-2">
                                  <button
                                    onClick={() => {
                                      setNodeAdd(true);
                                      setMaster(templeList);
                                      console.log("temple List", templeList);
                                    }}
                                    className="btn btn-primary gap-2"
                                  >
                                    Node
                                    <i className="fa-regular fa-paper-plane ms-2"></i>
                                  </button>

                                  {/* Show Pump Button only if templeList.pump is NOT available */}
                                  {!templeList.PumpDetails && (
                                    <button
                                      onClick={() => {
                                        // setModalShow(false);
                                        setAddPump(true);
                                        setMaster(templeList);
                                      }}
                                      className="btn btn-primary"
                                    >
                                      Pump
                                      <i className="fa-regular fa-hourglass-half ms-2"></i>
                                    </button>
                                  )}
                                </div>
                              </div>

                              <div className="card-body card-body-height table-responsive">
                                {templeList?.NodeData &&
                                templeList.NodeData.length > 0 ? (
                                  <Table
                                    pagination={{
                                      total: templeList.NodeData.length,
                                      current: currentPage,
                                      pageSize: 5,
                                      showSizeChanger: true,
                                      onChange: onPageChange,
                                      onShowSizeChange: onShowSizeChange,
                                    }}
                                    columns={columnsNode}
                                    dataSource={dataSource}
                                  />
                                ) : (
                                  <p>No Master Node Data Available</p>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="row">
                            {/* Pump Details Table */}
                            <div className="col-lg-6 mt-3">
                              <div className="card">
                                <div className="card-header">
                                  <h5 className="card-title">Pump Details</h5>
                                </div>
                                <div className="card-body">
                                  <div className="table-responsive table-hover">
                                    {loading && (
                                      <div
                                        className="spinner-grow text-light m-2"
                                        role="status"
                                      />
                                    )}
                                    {error && <p>{error}</p>}
                                    {templeList?.PumpDetails &&
                                    templeList.PumpDetails.length > 0 ? (
                                      <Table
                                        pagination={{
                                          total: templeList.PumpDetails.length,
                                          pageSize: 5,
                                          showSizeChanger: true,
                                          onChange: onPageChange,
                                          onShowSizeChange: onShowSizeChange,
                                        }}
                                        columns={[
                                          {
                                            title: "#",
                                            dataIndex: "index",
                                            key: "index",
                                            render: (_, __, index) => index + 1,
                                          },
                                          {
                                            title: "Pump Name",
                                            dataIndex: "PumpName",
                                            key: "PumpName",
                                          },
                                          {
                                            title: "HP",
                                            dataIndex: "hp",
                                            key: "hp",
                                          },
                                          {
                                            title: "Phase",
                                            dataIndex: "Phase",
                                            key: "Phase",
                                          },
                                          {
                                            title: "Status",
                                            dataIndex: "Status",
                                            key: "Status",
                                            render: (status) => (
                                              <span
                                                className={`badge ${
                                                  status
                                                    ? "bg-success"
                                                    : "bg-danger"
                                                }`}
                                              >
                                                {status ? "Active" : "Inactive"}
                                              </span>
                                            ),
                                          },
                                          {
                                            title: "Pump ID",
                                            dataIndex: "PumpID",
                                            key: "PumpID",
                                          },
                                          {
                                            title: "Created By",
                                            dataIndex: "created_by",
                                            key: "created_by",
                                          },
                                          {
                                            title: "Action",
                                            dataIndex: "action",
                                            key: "action",
                                            render: (text, record) => (
                                              <div className="d-flex align-items-center">
                                                <div className="dropdown dropdown-action">
                                                  <button
                                                    className="btn-action-icon border-0"
                                                    data-bs-toggle="dropdown"
                                                    aria-expanded="false"
                                                  >
                                                    <i className="fas fa-ellipsis-v"></i>
                                                  </button>
                                                  <div className="dropdown-menu dropdown-menu-right">
                                                    <ul>
                                                      <li>
                                                        <button
                                                          className="dropdown-item"
                                                          onClick={() => {
                                                            console.log(
                                                              "record Pump Name",
                                                              record.PumpName
                                                            );
                                                            setPumpEdit(
                                                              record.PumpName
                                                            );
                                                            setEditHpPump(
                                                              record.hp
                                                            );
                                                            setEditNamePump(
                                                              record
                                                            );
                                                            setEditPumpModalOpen(
                                                              true
                                                            );
                                                          }}
                                                        >
                                                          <i className="far fa-edit me-2"></i>
                                                          Edit
                                                        </button>
                                                      </li>
                                                    </ul>
                                                  </div>
                                                </div>
                                              </div>
                                            ),
                                          },
                                        ]}
                                        dataSource={templeList.PumpDetails.map(
                                          (pump, index) => ({
                                            key: pump.PumpID || index,
                                            ...pump,
                                          })
                                        )}
                                      />
                                    ) : (
                                      <p>No Pump data is available</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* User Details Table */}
                            <div className="col-lg-6 mt-3">
                              <div className="card">
                                <div className="card-header">
                                  <h5 className="card-title">User Details</h5>
                                </div>
                                <div className="card-body">
                                  <div className="table-responsive table-hover">
                                    {loading && (
                                      <div
                                        className="spinner-grow text-light m-2"
                                        role="status"
                                      />
                                    )}
                                    {error && <p>{error}</p>}
                                    {templeList.UserDetails &&
                                    templeList.UserDetails.length > 0 ? (
                                      <Table
                                        pagination={{
                                          total: templeList.UserDetails.length,
                                          pageSize: 5,
                                          showSizeChanger: true,
                                          onChange: onPageChange,
                                          onShowSizeChange: onShowSizeChange,
                                        }}
                                        columns={[
                                          {
                                            title: "ID",
                                            dataIndex: "ID",
                                            key: "ID",
                                          },
                                          {
                                            title: "Name",
                                            dataIndex: "Name",
                                            key: "Name",
                                          },
                                          {
                                            title: "City",
                                            dataIndex: "City",
                                            key: "City",
                                          },
                                          {
                                            title: "Address",
                                            dataIndex: "Address",
                                            key: "Address",
                                          },
                                          {
                                            title: "Location",
                                            dataIndex: "GeoLocation",
                                            key: "GeoLocation",
                                            render: (geo) => {
                                              const [latitude, longitude] = geo
                                                ? geo.split(",")
                                                : ["", ""];
                                              return latitude && longitude ? (
                                                <a
                                                  href={`https://www.google.com/maps?q=${latitude},${longitude}`}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  style={{ color: "blue" }}
                                                >
                                                  View Location
                                                </a>
                                              ) : (
                                                "N/A"
                                              );
                                            },
                                          },
                                          {
                                            title: "Email",
                                            dataIndex: "Email",
                                            key: "Email",
                                          },
                                          {
                                            title: "Action",
                                            dataIndex: "action",
                                            key: "action",
                                            render: (text, record) => (
                                              <div className="d-flex align-items-center">
                                                <div className="dropdown dropdown-action">
                                                  <button
                                                    className="btn-action-icon border-0"
                                                    data-bs-toggle="dropdown"
                                                    aria-expanded="false"
                                                  >
                                                    <i className="fas fa-ellipsis-v"></i>
                                                  </button>
                                                  <div className="dropdown-menu dropdown-menu-right">
                                                    <ul>
                                                      <li>
                                                        <button
                                                          className="dropdown-item text-danger"
                                                          onClick={() => {
                                                            console.log(
                                                              "Modal clicked",
                                                              record
                                                            );
                                                            setRemoveUserID(
                                                              record
                                                            );
                                                            setRemoveUserModalOpen(
                                                              true
                                                            );
                                                          }}
                                                        >
                                                          <i className="far fa-trash-alt me-2"></i>
                                                          Remove
                                                        </button>
                                                      </li>
                                                    </ul>
                                                  </div>
                                                </div>
                                              </div>
                                            ),
                                          },
                                        ]}
                                        dataSource={templeList.UserDetails.map(
                                          (user) => ({
                                            key: user.ID,
                                            ...user,
                                          })
                                        )}
                                      />
                                    ) : (
                                      <p>No User Details is available</p>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Modal 2 */}
        {EditModal && (
          <UserDetailEdit
            editTemple={editTemple}
            setEditModal={setEditModal}
            setEditTemple={setEditTemple}
          />
        )}

        {/* Modal 3 */}
        {NodeAdd && (
          <MasterAdd
            setNodeAdd={setNodeAdd}
            setModalShow={setModalShow}
            master={master}
            setEditModal={setEditModal}
            setHideContent={setHideContent}
          />
        )}

        {/* modal 4 */}
        {AddPump && (
          <Modal
            open={AddPump}
            centered
            width={500}
            footer={null}
            destroyOnClose
            title={
              <div className="d-flex align-items-center gap-2">
                <h5 className="mb-0 text-uppercase">Pump Add</h5>
              </div>
            }
            onCancel={() => {
              setAddPump(false);
            }}
          >
            <form onSubmit={handleSubmit} className="mt-4 mb-4">
              <div className="form-group">
                <label>Pump Name</label>
                <input
                  type="text"
                  name="pumpName"
                  value={pumpName}
                  onChange={(e) => setPumpName(e.target.value)}
                  onBlur={(e) => validateField(e.target.name, e.target.value)}
                  className="form-control"
                  placeholder="Enter Pump Name"
                />
                {errors.pumpName && (
                  <small className="error-text">{errors.pumpName}</small>
                )}
              </div>

              <div className="form-group">
                <label>Pump ID</label>
                <input
                  type="text"
                  name="pumpID"
                  value={pumpID}
                  onChange={(e) => setPumpID(e.target.value)}
                  onBlur={(e) => validateField(e.target.name, e.target.value)}
                  className="form-control"
                  placeholder="Enter Pump ID"
                />
                {errors.pumpID && (
                  <small className="error-text">{errors.pumpID}</small>
                )}
              </div>

              <div className="form-group">
                <label>HP</label>
                <input
                  type="text"
                  name="HP"
                  value={pumpHP}
                  onChange={(e) => setPumpHP(e.target.value)}
                  onBlur={(e) => validateField(e.target.name, e.target.value)}
                  className="form-control"
                  placeholder="Enter Pump HP"
                />
                {errors.pumpHP && (
                  <small className="error-text">{errors.pumpHP}</small>
                )}
              </div>

              <div className="form-group">
                <label>Phase Type</label>
                <select
                  name="pumpPhase"
                  value={pumpPhase}
                  onChange={(e) => setPumpPhase(e.target.value)}
                  onBlur={(e) => validateField(e.target.name, e.target.value)}
                  className="form-control"
                >
                  <option value="">Select Phase</option>
                  <option value="Single Phase">Single Phase</option>
                  <option value="Three Phase">Three Phase</option>
                </select>
                {errors.pumpPhase && (
                  <small className="error-text">{errors.pumpPhase}</small>
                )}
              </div>

              <button type="submit" className="btn btn-success w-100 mt-2">
                Submit
              </button>
            </form>
          </Modal>
        )}

        {/* Modal for view node details */}
        {/* <div
            className="modal fade"
            id="exampleModal"
            tabIndex="-1"
            aria-labelledby="exampleModalLabel"
            aria-hidden="true"
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="exampleModalLabel">
                    Node Details
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  {NodeModalData ? (
                    <>
                      <div className="d-flex gap-5 mb-3 text-dark">
                        <p>
                          <strong>Node ID:</strong>{" "}
                          {NodeModalData?.NodeID || "N/A"}
                        </p>
                        <p>
                          <strong>Valves Count:</strong>{" "}
                          {NodeModalData?.valves?.length || 0}
                        </p>
                      </div>

                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Valve Name</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {NodeModalData?.valves?.length > 0 ? (
                            NodeModalData.valves.map((valve, index) => (
                              <tr key={valve.ValveID || index}>
                                <td>{index + 1}</td>
                                <td>
                                  {editIndex === index ? (
                                    <input
                                      type="text"
                                      className="form-control"
                                      value={editedValves[index]?.name || ""}
                                      onChange={(e) =>
                                        handleInputChange(
                                          index,
                                          "name",
                                          e.target.value
                                        )
                                      }
                                    />
                                  ) : (
                                    valve.name
                                  )}
                                </td>
                                <td>
                                  <div className="form-check form-switch">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      checked={
                                        editedValves[index]?.status === "Active"
                                      }
                                      disabled={editIndex !== index}
                                      onChange={(e) =>
                                        handleInputChange(
                                          index,
                                          "status",
                                          e.target.checked ? "Active" : "Inactive"
                                        )
                                      }
                                    />
                                  </div>
                                </td>
                                <td>
                                  {editIndex === index ? (
                                    <button
                                      className="btn btn-sm btn-success"
                                      onClick={handleUpdate}
                                    >
                                      <i className="fas fa-save"></i> Update
                                    </button>
                                  ) : (
                                    <button
                                      className="btn btn-sm btn-primary"
                                      onClick={() => {
                                        setEditIndex(index);
                                        setNodeValveEditValue(valve);
                                      }}
                                    >
                                      <i className="far fa-edit"></i> Edit
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="4" className="text-center">
                                No valves available
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </>
                  ) : (
                    <p>No data available</p>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    data-bs-dismiss="modal"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div> */}

        <Drawer
          open={!!NodeModalData}
          title="Node Details"
          width={720}
          onClose={() => setNodeModalData(null)}
          destroyOnClose
        >
          {NodeModalData ? (
            <>
              <div className="d-flex gap-5 mb-3 text-dark">
                <p>
                  <strong>Node ID:</strong> {NodeModalData.NodeID || "N/A"}
                </p>
                <p>
                  <strong>Valves Count:</strong>{" "}
                  {NodeModalData.valves?.length || 0}
                </p>
              </div>

              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Valve Name</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {NodeModalData.valves?.length > 0 ? (
                    NodeModalData.valves.map((valve, index) => (
                      <tr key={valve.ValveID || index}>
                        <td>{index + 1}</td>

                        <td>
                          {editIndex === index ? (
                            <input
                              className="form-control"
                              value={editedValves[index]?.name || ""}
                              onChange={(e) =>
                                handleInputChange(index, "name", e.target.value)
                              }
                            />
                          ) : (
                            valve.name
                          )}
                        </td>

                        <td>
                          <Switch
                            checked={editedValves[index]?.status === "Active"}
                            disabled={editIndex !== index}
                            checkedChildren="ON"
                            unCheckedChildren="OFF"
                            onChange={(checked) =>
                              handleInputChange(
                                index,
                                "status",
                                checked ? "Active" : "Inactive"
                              )
                            }
                          />
                        </td>

                        <td>
                          {editIndex === index ? (
                            <Button
                              size="small"
                              type="primary"
                              onClick={handleUpdate}
                            >
                              Update
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              onClick={() => {
                                setEditIndex(index);
                                setNodeValveEditValue(valve);
                              }}
                            >
                              Edit
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center">
                        No valves available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          ) : (
            <p>No data available</p>
          )}
        </Drawer>

        {/* modal for view node Edit */}
        <Modal
          open={editNodeModalOpen}
          title="Edit Node"
          centered
          width={500}
          onCancel={() => setEditNodeModalOpen(false)}
          onOk={() => {
            handleNameSubmit();
            setEditNodeModalOpen(false);
          }}
          okText="Save Changes"
          cancelText="Close"
          destroyOnClose
        >
          {/* Node Name Input */}
          <div className="mb-3">
            <label className="form-label">Node Name</label>
            <input
              type="text"
              className="form-control"
              value={NodeEdit}
              onChange={handleNameChange}
            />
          </div>
        </Modal>

        {/* modal for Pump Edit */}
        <Modal
          open={editPumpModalOpen}
          title="Edit Pump"
          centered
          width={500}
          destroyOnClose
          onCancel={() => setEditPumpModalOpen(false)}
          onOk={() => {
            handlePumpNameSubmit();
            setEditPumpModalOpen(false);
          }}
          okText="Save Changes"
          cancelText="Close"
        >
          {/* Pump Name */}
          <div className="mb-3">
            <label className="form-label">Pump Name</label>
            <input
              type="text"
              className="form-control"
              value={PumpEdit}
              placeholder="Enter the name of the pump"
              onChange={handlePumpNameChange}
            />
          </div>

          {/* HP */}
          <div className="mb-3">
            <label className="form-label">HP</label>
            <input
              type="text"
              className="form-control"
              placeholder="Enter the HP"
              value={EditHpPump}
              onChange={handlePumpHPChange}
            />
          </div>
        </Modal>

        <Modal
          open={removeUserModalOpen}
          title="Edit User Master"
          centered
          destroyOnClose
          okText="Remove"
          cancelText="Close"
          onCancel={() => setRemoveUserModalOpen(false)}
          onOk={() => {
            handleRemoveUserSubmit();
            setRemoveUserModalOpen(false);
          }}
        >
          <p>Do you want to remove the user from this master?</p>
        </Modal>
      </div>
    </>
  );
};

export default AllUserDetails;
